/** Jedna ceremonia: włącznik, godzina/próg, ostatnie i następne uruchomienie. */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { NumberField, settingsKeys } from "./SettingsPage";
import type { Ceremony, CeremonyConfig } from "./types";

function formatMoment(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return date.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
}

export function CeremonyCard({
  ceremony,
  draft,
  onChange,
}: {
  ceremony: Ceremony;
  draft: CeremonyConfig | undefined;
  onChange: (config: CeremonyConfig) => void;
}) {
  const qc = useQueryClient();
  const config: CeremonyConfig = draft ?? ceremony.config;

  const run = useMutation({
    mutationFn: () => api.runCeremony(ceremony.name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.ceremonies });
      void qc.invalidateQueries({ queryKey: ["chat", "messages"] });
    },
  });

  const patch = (part: CeremonyConfig) => onChange({ ...config, ...part });
  const has = (key: keyof CeremonyConfig) => ceremony.config[key] !== undefined;

  return (
    <li className="row row--block">
      <div className="row__head" style={{ cursor: "default" }}>
        <label className="row__check">
          <input
            type="checkbox"
            checked={config.enabled !== false}
            onChange={(e) => patch({ enabled: e.target.checked })}
            aria-label={`Włącz ceremonię ${ceremony.title}`}
          />
        </label>
        <div className="row__main">
          <span className="row__title">{ceremony.title}</span>
          <span className="row__meta">
            <span>{ceremony.schedule}</span>
            <span>następne: {formatMoment(ceremony.next_run)}</span>
          </span>
        </div>
        <div className="row__actions">
          <button className="btn btn--small" disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? "…" : "Uruchom"}
          </button>
        </div>
      </div>

      <div className="drawer">
        <div className="field-row">
          {has("hour") && (
            <NumberField
              label="godzina"
              value={config.hour}
              min={0}
              max={23}
              onChange={(hour) => patch({ hour })}
            />
          )}
          {has("minute") && (
            <NumberField
              label="minuta"
              value={config.minute}
              min={0}
              max={59}
              onChange={(minute) => patch({ minute })}
            />
          )}
          {has("every_hours") && (
            <NumberField
              label="co ile godzin"
              value={config.every_hours}
              min={1}
              max={24}
              onChange={(every_hours) => patch({ every_hours })}
            />
          )}
          {has("every_minutes") && (
            <NumberField
              label="co ile minut"
              value={config.every_minutes}
              min={1}
              max={120}
              onChange={(every_minutes) => patch({ every_minutes })}
            />
          )}
          {has("threshold_hours") && (
            <NumberField
              label="próg (godziny)"
              value={config.threshold_hours}
              min={1}
              max={720}
              onChange={(threshold_hours) => patch({ threshold_hours })}
            />
          )}
          {has("days_ahead") && (
            <NumberField
              label="wyprzedzenie (dni)"
              value={config.days_ahead}
              min={0}
              max={60}
              onChange={(days_ahead) => patch({ days_ahead })}
            />
          )}
          {has("threshold_days") && (
            <NumberField
              label="próg (dni)"
              value={config.threshold_days}
              min={1}
              max={90}
              onChange={(threshold_days) => patch({ threshold_days })}
            />
          )}
        </div>

        {ceremony.last_run && (
          <p className="muted">
            Ostatnio: {formatMoment(ceremony.last_run.started_at)} —{" "}
            {ceremony.last_run.silent ? "cisza (nic do zgłoszenia)" : ceremony.last_run.message}
          </p>
        )}
        {run.data && (
          <p className="muted">
            {run.data.silent ? "Cisza — nic do zgłoszenia." : `Wysłano: ${run.data.message}`}
          </p>
        )}
        {run.error && <p className="error">Nie udało się uruchomić ceremonii.</p>}
      </div>
    </li>
  );
}
